import { ContentfulClientApi, EntryCollection, createClient } from 'contentful';
import isEqual from 'lodash/isEqual';
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';

import { ContentfulFilters, fetchEntries } from 'src/api/contentful/fetchEntries';
import { CancelablePromise, makeCancelable } from 'src/services/utils';
import { Environment } from 'src/utils/Environment';

/**
 * The props that are passed into the nested render function
 */
export type RenderPropFunc<T extends object> = (props: ContentfulFilterRenderProps<T>) => JSX.Element | null | Array<JSX.Element | null>;

/**
 * The props that are provided to ContentfulEntry
 */
export interface ProvidedProps<EntryType extends object = any> extends ContentfulFilters<EntryType> {
  // Allow defining the render props by either a prop or as children
  render?: RenderPropFunc<EntryType>;
  children?: RenderPropFunc<EntryType>;
}

export interface ContentfulEntryFilterState<EntryType extends object = any> {
  entries?: EntryCollection<EntryType>;
  isLoading: boolean;
  notFound: boolean;
  hasError: boolean;
}

/**
 * The props that are provided to the render function OR children function
 */
export type ContentfulFilterRenderProps<EntryType extends object> =
  ContentfulEntryFilterState<EntryType>
  & WithTranslation;

class ContentfulEntryFilter<EntryType extends object> extends React.PureComponent<ProvidedProps<EntryType> & WithTranslation, ContentfulEntryFilterState<RecursivePartial<EntryType>>> {
  static defaultProps: Partial<ProvidedProps<any> & WithTranslation> = {
    config: Environment.getCloverdotcomContentfulConfig(),
    include: 1,
  };

  readonly state = {
    isLoading: true,
    notFound: false,
    hasError: false,
  };

  private cancelableFetchEntries: CancelablePromise;

  private apiClient: ContentfulClientApi;

  constructor(props: ProvidedProps<EntryType> & WithTranslation, context?: any) {
    super(props, context);

    this.apiClient = createClient(this.props.config!);
  }

  componentDidMount() {
    this.fetchEntries();
  }

  componentWillUnmount() {
    this.cancelableFetchEntries.cancel();
  }

  componentDidUpdate(prevProps: Readonly<ProvidedProps<EntryType> & WithTranslation>) {
    if (prevProps.contentType !== this.props.contentType ||
      prevProps.include !== this.props.include ||
      prevProps.locale !== this.props.locale ||
      prevProps.config!.space !== this.props.config!.space ||
      prevProps.config!.host !== this.props.config!.host ||
      prevProps.linksToEntry !== this.props.linksToEntry ||
      prevProps.limit !== this.props.limit ||
      prevProps.skip !== this.props.skip ||
      prevProps.select !== this.props.select ||
      !isEqual(prevProps.filters, this.props.filters) ||
      prevProps.t !== this.props.t // If the i18n.language has changed
    ) {
      this.fetchEntries();
    }

    // TODO: compare filters and select objects in a smarter way
  }

  readonly onError = (err: any) => {
    const { contentType, config } = this.props;

    this.setState({ isLoading: false, hasError: true });
    window.console.error(`Problem loading ${contentType} from space ${config!.space}`, err);
  };

  render() {
    const { render, children, i18n, t, tReady } = this.props;

    if (!render && !children) {
      this.onError('No render props were provided to ContentfulEntry.');
      return null;
    }
    if (!!render && !!children) {
      this.onError('You may provide either a render function as a prop OR as a child to ContentfulEntry but not both.');
      return null;
    }

    const renderProps: ContentfulFilterRenderProps<EntryType> = { ...this.state, i18n, t, tReady };

    return render ? render(renderProps) : children!(renderProps);
  }

  private async fetchEntries() {
    const { i18n, locale, ...filters } = this.props;

    this.setState({ isLoading: true });

    this.cancelableFetchEntries = makeCancelable(fetchEntries<EntryType>(locale || i18n.language, filters, this.apiClient));
    try {
      const entries = await this.cancelableFetchEntries.promise;
      this.setState({
        entries,
        isLoading: false,
        notFound: !entries || !entries.items || !entries.items.length,
      });
    } catch (error) {
      if (error.isCanceled) return; // this means that the component has been unmounted
      // setting state on an unmounted component throws a warning in console
      this.onError(error);
    }
  }
}

const Translated = withTranslation()(ContentfulEntryFilter);

export {
  ContentfulEntryFilter as _ContentfulEntryFilter,
  Translated as ContentfulEntryFilter
};
